// pages/shop/ProductView.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';

import ReactImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

import { FaAnglesRight, FaCartPlus } from 'react-icons/fa6';
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';

import ProductCard from '../../components/products/ProductCard';
import Empty from '../../components/feedback/Empty';
import Loading from '../../components/feedback/Loading';
import utils from '../../utils';
import productServices from '../../services/ProductsServices';
import cartServices from '../../services/CartServices';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/UserContext';

function ProductView() {
    const { user } = useAuth()
    const { slug } = useParams();
    const cartCtx = useCart();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [quantity, setQuantity] = useState(1);
    const [productImages, setProductImages] = useState([]);
    const [additionalImages, setAdditionalImages] = useState([])
    const [activeTab, setActiveTab] = useState(0);

    const [cartLoading, setCartLoading] = useState(false);

    const [relatedProducts, setRelatedProducts] = useState({ results: [], next: null });
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [relatedError, setRelatedError] = useState(null);
    const [relatedHasMore, setRelatedHasMore] = useState(true);
    const isRelatedFetchingRef = useRef(false);


    // Fetch main product
    const fetchProduct = useCallback(async () => {
        if (!slug) return;
        setLoading(true);
        setError(null);
        try {
            const res = await productServices.getProduct(slug);
            setProduct(res.data);
        } catch (err) {
            console.error('Failed to load product:', err);
            setError('Product not found');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    // fetch related prods
    const fetchRelated = useCallback(async (nextUrl = null, productType) => {
        if (!productType || isRelatedFetchingRef.current) return;

        isRelatedFetchingRef.current = true;
        setRelatedLoading(true);
        setRelatedError(null);

        try {
            let res;
            if (nextUrl) {
                const url = new URL(nextUrl);
                res = await productServices.getProducts(url.search);
            } else {
                res = await productServices.getProducts(`?product_type=${productType}`);
            }

            setRelatedProducts(prev => ({
                results: nextUrl ? [...prev.results, ...res.data.results] : res.data.results,
                next: res.data.next
            }));
            setRelatedHasMore(!!res.data.next);
        } catch (err) {
            console.error('Failed to load related products:', err);
            setRelatedError(err);
            setRelatedHasMore(false);
        } finally {
            setRelatedLoading(false);
            isRelatedFetchingRef.current = false;
        }
    }, []);

    // Sync data on slug or product change
    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    useEffect(() => {
        if (product) {
            // Set images
            if (product.image) {
                let extraImgs = []
                const additionalImgs = [];
                const img = {
                    original: `${utils.baseUrl}${product.image}`,
                    thumbnail: `${utils.baseUrl}${product.image}`,
                };
                extraImgs.push(img)

                product?.images?.forEach(element => {
                    const img = {
                        original: `${utils.baseUrl}${element?.url}`,
                        thumbnail: `${utils.baseUrl}${element?.url}`,
                    }
                    if (element.is_additional) {
                        additionalImgs.push(img)
                    } else {
                        extraImgs.push(img)
                    }
                });
                setProductImages(extraImgs);
                setAdditionalImages(additionalImgs)
            }
            // Fetch related
            if (product?.product_type) {
                // Reset & fetch first page
                setRelatedProducts({ results: [], next: null });
                setRelatedHasMore(true);
                fetchRelated(null, product.product_type);
            }

        }
    }, [product, fetchRelated]);

    useEffect(() => {
        if (!relatedHasMore || !relatedProducts.next) return;

        const handleScroll = () => {
            if (isRelatedFetchingRef.current) return;

            const { scrollTop, scrollHeight } = document.documentElement;
            const windowHeight = window.innerHeight;


            if (scrollTop + windowHeight + 200 >= scrollHeight) {
                fetchRelated(relatedProducts.next, product?.product_type);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [relatedHasMore, relatedProducts.next, fetchRelated, product?.product_type]);

    // Quantity handlers
    const reduceItemQuantity = () => {
        if (quantity <= 1) {
            toast.error('Minimum quantity is 1');
            return;
        }
        setQuantity(q => q - 1);
    };

    const addItemQuantity = () => {
        if (product && quantity >= product.quantity) {
            toast.error(`Only ${product.quantity} in stock.`);
            return;
        }
        setQuantity(q => q + 1);
    };

    // Tabs
    const tabs = ['Item Info', 'Additional Info'];

    if (loading) {
        return (
            <div className='py-10'>
                <Loading />
            </div>
        );
    }

    if (error) {
        return (
            <section className="py-10 space-y-5">
                <div className="flex justify-center">
                    <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">

                        <div className="absolute top-[-20%] right-[-20%] w-64 h-64 bg-primary/20 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-[-20%] left-[-20%] w-64 h-64 bg-accent-red/20 rounded-full blur-3xl"></div>
                        <div className="relative not-found-bg z-5 w-full h-full bg-center bg-cover bg-no-repeat" data-alt="3D playful illustration of a floating cardboard box in space with orange accent lighting">
                            <div className="absolute inset-0 bg-gradient-to-t from-background-light/80 to-transparent dark:from-background-dark/80"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h1 className="text-[120px] md:text-[160px] font-extrabold text-white mix-blend-overlay opacity-90 leading-none select-none">404</h1>
                        </div>
                    </div>
                </div>
                <div className="container mx-auto px-4 text-center">
                    <p className="text-red-600">{error}</p>
                </div>
                <div className="flex justify-center">
                    <div className="flex flex-col sm:flex-row gap-4 w-full justify-center max-w-[480px]">
                        <Link className="flex flex-1 cursor-pointer items-center justify-center rounded-xl h-12 px-6 bg-primary hover:bg-orange-600 text-white text-base font-bold leading-normal tracking-[0.015em] transition-all shadow-lg shadow-primary/25 transform hover:-translate-y-0.5" to="/">
                            Return to Homepage
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    const updateCart = () => {
        if (user) {
            if (user?.is_admin || user?.is_vendor) {
                toast.error("Account not allowed to perform this!")
                return
            }
        }
        if (!product) return;

        setCartLoading(true);
        const data = {
            product: product.id,
            quantity: quantity,
        };

        const isAlreadyInCart = cartCtx.inCart(product.slug);
        const toastMessage = isAlreadyInCart
            ? "Updating item quantity..."
            : "Adding item to cart...";

        const submitToast = toast.loading(toastMessage);

        cartServices.addCartItem(data)
            .then(res => {
                const result = res.data;

                cartCtx.setNumItems(result.num_items || 0);
                cartCtx.setItemSlugs(
                    (result.cart_items || []).map(item => ({
                        slug: item.product?.slug,
                        id: item.product?.id,
                    }))
                );

                toast.success(result.message || 'Item added to cart!', {
                    id: submitToast,
                });

                setTimeout(() => {
                    navigate('/cart');
                }, 600);
            })
            .catch(err => {
                console.error('Add to cart error:', err);
                const errorMsg =
                    err.response?.data?.message ||
                    err.response?.data?.detail ||
                    'Failed to update cart. Please try again.';

                toast.error(errorMsg, { id: submitToast });
            })
            .finally(() => {
                setCartLoading(false);
            });
    };



    return (
        <div>
            <Helmet>
                <title>{product?.name ? String(product.name) : 'Product'} - {utils.siteName}</title>

                {/* Primary description */}
                <meta
                    name="description"
                    content={
                        product?.description
                            ? String(product.description).replace(/<[^>]*>/g, '').substring(0, 155) + '...'
                            : 'Shop this item on Kenya\'s trusted online marketplace. Fast delivery & secure payments.'
                    }
                />

                {/* Open Graph */}
                {product?.image && typeof product.image === 'string' && (
                    <meta property="og:image" content={`${utils.baseUrl}${product.image}`} />
                )}
                <meta property="og:title" content={product?.name && typeof product.name === 'string' ? product.name : 'Product'} />
                <meta
                    property="og:description"
                    content={
                        product?.description && typeof product.description === 'string'
                            ? product.description.replace(/<[^>]*>/g, '').substring(0, 155)
                            : 'Buy online from verified Kenyan vendors. Nationwide delivery.'
                    }
                />
                <meta property="og:type" content="product" />
                <meta
                    property="og:url"
                    content={typeof window !== 'undefined' ? window.location.href : ''}
                />
                <meta property="og:site_name" content={typeof utils.siteName === 'string' ? utils.siteName : 'Kenya Marketplace'} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={product?.name && typeof product.name === 'string' ? product.name : 'Product'} />
                <meta
                    name="twitter:description"
                    content={
                        product?.description && typeof product.description === 'string'
                            ? product.description.replace(/<[^>]*>/g, '').substring(0, 155)
                            : 'Shop online in Kenya. Trusted vendors, fast delivery.'
                    }
                />
                {product?.image && typeof product.image === 'string' && (
                    <meta name="twitter:image" content={`${utils.baseUrl}${product.image}`} />
                )}

                {/* Canonical */}
                <link
                    rel="canonical"
                    href={typeof window !== 'undefined' ? window.location.href : ''}
                />

                {/* Product price */}
                {/* {product?.price != null && (
                    <>
                        <meta property="product:price:amount" content={Math.floor(Number(product?.price)).toString()} />
                        <meta property="product:price:currency" content="KES" />
                    </>
                )} */}

                {/* JSON-LD Schema */}
                {product && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Product',
                            name: product.name && typeof product.name === 'string' ? product.name : '',
                            image: product.image && typeof product.image === 'string' ? `${utils.baseUrl}${product.image}` : '',
                            description:
                                product.description && typeof product.description === 'string'
                                    ? product.description.replace(/<[^>]*>/g, '').substring(0, 500)
                                    : '',
                            offers: {
                                '@type': 'Offer',
                                // price: product.price && typeof product.price !== 'symbol' ? String(product.price) : '',
                                priceCurrency: 'KES',
                                availability: 'https://schema.org/InStock',
                                url: typeof window !== 'undefined' ? window.location.href : '',
                            },
                        })}
                    </script>
                )}

            </Helmet>



            <main className="container mx-auto px-4 py-4">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4 lg:gap-6 mb-7">
                    <div className="md:w-6/12">
                        <div className="flex justify-center overflow-x-auto px-2">
                            <ReactImageGallery
                                items={productImages}
                                showThumbnails={window.innerWidth > 768 ? true : false}
                                showBullets={window.innerWidth < 768 ? true : false}
                                thumbnailPosition="bottom"
                                lazyLoad={true}
                            />
                        </div>
                    </div>

                    <div className="md:w-6/12">
                        <h1 className="text-4xl text-primary font-bold mb-4">{product?.name}</h1>
                        <div className="space-y-3 mb-7">
                            {product && (
                                <p>
                                    Price: <span className="ml-3 font-bold text-secondary text-lg">KSH. {utils.toCommas(product.price)}</span>
                                </p>
                            )}

                            <p className="uppercase font-bold text-lg">Quick Info</p>

                            <p>
                                Product Type:
                                <Link
                                    to={`/products?product_type=${product?.product_type}`}
                                    className="ml-3 uppercase font-bold hover:underline hover:cursor-pointer"
                                >
                                    {product?.product_type_name || '-'}
                                </Link>
                            </p>

                            {product?.brand_name && (
                                <p>
                                    Brand:
                                    <Link
                                        to={`/products?brand=${product?.brand}`}
                                        className="ml-3 uppercase font-bold hover:underline hover:cursor-pointer"
                                    >
                                        {product?.brand_name}
                                    </Link>
                                </p>
                            )}

                            {product?.model_name && (
                                <p>
                                    Model:
                                    <Link
                                        to={`/products?model=${product?.model}`}
                                        className="ml-3 uppercase font-bold hover:underline hover:cursor-pointer"
                                    >
                                        {product?.model_name}
                                    </Link>
                                </p>
                            )}
                            {product?.store_name && (
                                <p>
                                    Store:
                                    <Link
                                        to={`/products?search=${product?.store_name}`}
                                        className="ml-3 uppercase font-bold hover:underline hover:cursor-pointer"
                                    >
                                        {product?.store_name}
                                    </Link>
                                </p>
                            )}

                            {product?.description && (
                                <>
                                    <p>Description:</p>
                                    <div className="prose prose-p:leading-[1.5] prose-li:leading-[1.5] max-w-none" dangerouslySetInnerHTML={{ __html: product?.description }} />
                                </>
                            )}
                            <div className="py-4">
                                {product?.quantity > 0 ? (
                                    <div className="flex items-center gap-5">
                                        <div className="flex items-center">
                                            <button
                                                onClick={reduceItemQuantity}
                                                className="btn btn-error btn-circle"
                                                type="button"
                                                aria-label="Decrease quantity"
                                            >
                                                <AiOutlineMinus />
                                            </button>
                                            <span className="mx-3 text-lg font-bold">{quantity}</span>
                                            <button
                                                onClick={addItemQuantity}
                                                className="btn btn-success btn-circle"
                                                type="button"
                                                aria-label="Increase quantity"
                                            >
                                                <AiOutlinePlus />
                                            </button>
                                        </div>

                                        <button
                                            onClick={() => updateCart()}
                                            className="flex-1 flex justify-center gap-3 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors duration-300"
                                            disabled={cartLoading}
                                        >
                                            {cartLoading ? 'Adding...' : (
                                                <>
                                                    <FaCartPlus className="h-5 w-5" />
                                                    Add To Cart
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-red-500 font-semibold">Out of stock!</p>
                                )}
                            </div>

                            <div className="flex justify-start mb-6">
                                {/* Tabs */}
                                <div className="tabs tabs-boxed">
                                    {tabs.map((tab, idx) => (
                                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                        <a
                                            key={idx}
                                            className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                            onClick={() => setActiveTab(idx)}
                                            role="tab"
                                            aria-selected={activeTab === idx}
                                        >
                                            {tab}
                                        </a>
                                    ))}
                                </div>
                            </div>

                            {activeTab === 0 && (
                                <div className="space-y-3">
                                    <p>
                                        Quantity: <span className="ml-3 font-bold">{product?.quantity || 0}</span>
                                    </p>
                                    <p>
                                        Condition: <span className="ml-3 font-bold">{product?.condition || '-'}</span>
                                    </p>
                                    {product?.manufacture_year && (
                                        <p>
                                            Manufacture Year: <span className="ml-3 font-bold">{product?.manufacture_year || '-'}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            {activeTab === 1 && (
                                <div className="space-y-3">
                                    {product?.warranty_info && (
                                        <p>
                                            Warranty Info: <span className="ml-3 font-bold text-pretty">{product?.warranty_info || '-'}</span>
                                        </p>
                                    )}
                                    {/* {product?.sku && (
                                        <p>
                                            SKU: <span className="ml-3 font-bold">{product?.sku || 0}</span>
                                        </p>
                                    )} */}
                                    <p>
                                        Added: <span className="ml-3 font-bold">
                                            {product?.creation_time ? new Date(product.creation_time).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                                        </span>
                                    </p>
                                    {additionalImages?.length ? (
                                        <>
                                            <p ><span className='font-bold mr-2'>Additional Images:</span> ({additionalImages?.length})</p>
                                            <div className="flex justify-center overflow-x-auto">
                                                <ReactImageGallery
                                                    items={additionalImages}
                                                    showThumbnails={window.innerWidth > 768 ? true : false}
                                                    showBullets={window.innerWidth < 768 ? true : false}
                                                    thumbnailPosition="bottom"
                                                    lazyLoad={true}
                                                />
                                            </div>
                                        </>
                                    ):null}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div className="mb-9">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="text-2xl font-bold text-primary">Related Products</h4>
                        {product?.product_type && (
                            <Link
                                to={`/products?product_type=${product.product_type}`}
                                className="text-lg font-bold flex items-center gap-2 text-secondary hover:text-orange-600"
                            >
                                View More <FaAnglesRight />
                            </Link>
                        )}
                    </div>

                    {relatedLoading && (!relatedProducts?.results || relatedProducts.results.length === 0) ? (
                        <Loading />
                    ) : relatedError ? (
                        <Empty objectName="Related Products" />
                    ) : relatedProducts?.results?.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {relatedProducts.results.map((p, idx) => (
                                    <ProductCard key={p?.id || idx} product={p} />
                                ))}
                            </div>

                            {relatedLoading && (
                                <div className="flex justify-center py-4">
                                    <Loading />
                                </div>
                            )}
                        </>
                    ) : (
                        <Empty objectName="Related Products" />
                    )}
                </div>
            </main>
        </div>
    );
}

export default ProductView;